import React, { useState } from 'react';
import useUomConvert from 'hooks/useUomConvert';
import { useFormikContext } from 'formik';
import FormGrid, { FormRowConfig } from 'components/FormGrid';
import { COL_SPAN_ONE_FOURTH } from 'components/FormGrid/constants';
import {
  FormValues,
  Product
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import InfoTooltip from 'components/InfoTooltip';
import { LayoutStrings } from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import { Input, Row } from 'syngenta-digital-cropwise-react-ui-kit';
import formatNumber from 'screens/Invoices/helpers/formatNumber';
import { message } from 'antd';

import { isEqual } from 'lodash';
import { RECENTLY_UPDATED_CLASSNAMES } from '../ProductsTable/constants';
import { useProductStep } from '../../hooks/useProductStep';
import useUpdateWithFeedback from '../ProductsTable/useUpdateWithFeedback';
import {
  calculateApplicationValues,
  calculateTotalProductsByKey,
  differenceBetweenApplications,
  getNonCarrierLiquidProducts,
  recalculateApplicationValues
} from '../ProductsTable/helpers';
import { TankInfoFieldNames } from './types';

const TankInformationSection: React.FC = () => {
  const { values, errors, setFieldValue } = useFormikContext<FormValues>();
  const convertFn = useUomConvert();

  const {
    isUpdatingTankInfo,
    update,
    recentlyUpdated,
    setRecentlyUpdated
  } = useProductStep();

  const handleMessageClose = (messageKey?: string) => {
    update({ isUpdatingTankInfo: false, field: null });
    if (messageKey) {
      message.destroy(messageKey);
    } else {
      // Fallback to the default key for backward compatibility
      message.destroy('tank-info-updated');
    }
  };

  const { executeUpdateWithFeedback } = useUpdateWithFeedback({
    setRecentlyUpdated,
    update,
    handleMessageClose
  });

  // Focus state management for inputs
  const [focusedInput, setFocusedInput] = useState<string | null>(null);

  // Pending edits state - stores temporary values during input editing
  const [pendingEdits, setPendingEdits] = useState<Record<string, string>>({});

  const isAllFieldsFilled = [
    values.tankSize,
    values.tankCount,
    values.overallRatePerAcre,
    values.totalProduct
  ].every(tankInfo => typeof tankInfo === 'number');

  const getDisplayValue = (value: number | undefined, fieldName: string) => {
    const isFocused = focusedInput === fieldName;
    if (isFocused && pendingEdits[fieldName] !== undefined) {
      return pendingEdits[fieldName];
    }
    return isFocused && value !== undefined
      ? value.toString()
      : formatNumber(value);
  };

  const handleReset = () => {
    setFieldValue('products', values.products);
    setFieldValue('tankSize', values.tankSize);
    setFieldValue('tankCount', values.tankCount);
    setFieldValue('overallRatePerAcre', values.overallRatePerAcre);
    setFieldValue('totalProduct', values.totalProduct);
  };

  const isAtLeastTwoFieldsFilled = (
    fieldName: TankInfoFieldNames,
    value: number
  ) => {
    const fields: TankInfoFieldNames[] = [
      'tankSize',
      'tankCount',
      'overallRatePerAcre',
      'totalProduct'
    ];

    // Build a snapshot of the four tank fields taking into account the new value
    const snapshotValues = fields.map(name =>
      name === fieldName
        ? value
        : (values[name as keyof FormValues] as number | undefined)
    );

    return (
      snapshotValues.filter(v => typeof v === 'number' && Number.isFinite(v))
        .length >= 2
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const withoutNegativeSigns = value.replaceAll(/[-\u2212]/g, '');
    setPendingEdits(prev => ({ ...prev, [name]: withoutNegativeSigns }));
  };

  const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name } = e.target;
    setFocusedInput(name);
    const fieldValue = values[name as keyof FormValues];
    setPendingEdits(prev => ({
      ...prev,
      [name]:
        typeof fieldValue === 'number' || typeof fieldValue === 'string'
          ? String(fieldValue)
          : ''
    }));
  };

  const isNotReadyToCalculateDependingValues = (
    fieldName: TankInfoFieldNames,
    value: number
  ) => {
    if (
      !isAtLeastTwoFieldsFilled(fieldName, value) &&
      fieldName !== 'overallRatePerAcre' &&
      fieldName !== 'totalProduct'
    ) {
      return true;
    }
    return false;
  };

  const handleUpdate = (fieldName: TankInfoFieldNames, value: number) => {
    // Set the field value first
    setFieldValue(fieldName, value);

    if (isNotReadyToCalculateDependingValues(fieldName, value)) {
      return { fields: [], products: {} };
    }

    const calculateFn = isAllFieldsFilled
      ? recalculateApplicationValues
      : calculateApplicationValues;

    const updatedApplication = calculateFn(values, fieldName, value, convertFn);

    const effectiveTankFields = [
      fieldName === 'tankSize' ? value : updatedApplication.tankSize,
      fieldName === 'tankCount' ? value : updatedApplication.tankCount,
      fieldName === 'overallRatePerAcre'
        ? value
        : updatedApplication.overallRatePerAcre,
      fieldName === 'totalProduct' ? value : updatedApplication.totalProduct
    ];

    if (effectiveTankFields.every(v => !v)) {
      setFieldValue('tankSize', undefined);
      setFieldValue('tankCount', undefined);
      setFieldValue('overallRatePerAcre', undefined);
      setFieldValue('totalProduct', undefined);
      return { fields: [], products: {} };
    }

    if (isEqual(values, updatedApplication)) {
      handleReset();
      return { fields: [], products: {} };
    }

    const { changedFields, products } = differenceBetweenApplications(
      values,
      updatedApplication
    );

    setRecentlyUpdated({ fields: changedFields, products });

    if (changedFields.length > 0) {
      // eslint-disable-next-line no-restricted-syntax
      for (const changedFieldName of changedFields) {
        setFieldValue(
          changedFieldName,
          updatedApplication[changedFieldName as keyof FormValues]
        );
      }
    }

    setFieldValue('products', updatedApplication.products);

    return { fields: changedFields, products };
  };

  const validateCarrierValues = (
    fieldName: TankInfoFieldNames,
    value: number
  ) => {
    const calculateFn = isAllFieldsFilled
      ? recalculateApplicationValues
      : calculateApplicationValues;

    const updatedApplication = calculateFn(values, fieldName, value, convertFn);
    const carrier = updatedApplication.products.find(p => p.isCarrier);

    if (carrier) {
      // Carrier total product must be equal to or greater than 0
      return typeof carrier.totalProduct === 'number'
        ? carrier.totalProduct >= 0
        : false;
    }

    const sumOfTotalProducts = calculateTotalProductsByKey(
      getNonCarrierLiquidProducts(values.products),
      'totalProduct'
    );

    const overallTotalProduct =
      fieldName === 'totalProduct'
        ? value
        : updatedApplication.totalProduct ?? values.totalProduct;

    /*
      Overall Total Product must be greater than or equal to the sum of
      non-carrier liquid products total product
    */
    return (
      typeof overallTotalProduct === 'number' &&
      overallTotalProduct >= sumOfTotalProducts
    );
  };

  const setUpdatedField = (
    fieldName: TankInfoFieldNames,
    options?: { updateMessage: string }
  ) => {
    const currentValue = values[fieldName as keyof FormValues];

    const newValue = Number(pendingEdits[fieldName]);

    if (currentValue === newValue) {
      return;
    }

    executeUpdateWithFeedback({
      recentlyUpdatedData: {}, // Not used since skipRecentlyUpdated is true
      updateData: {
        isUpdatingTankInfo: true,
        field: {
          name: fieldName,
          newValue: Number(pendingEdits[fieldName]),
          currentValue: values[fieldName as keyof FormValues]
        }
      },
      messageContent: options?.updateMessage || 'Changes successful.',
      onUndo: () => handleReset(),
      skipRecentlyUpdated: true // Skip since handleUpdate already calls setRecentlyUpdated
    });
  };

  const showUpdateMessageIfChanged = (
    updatedRecentlyUpdated: any,
    inputFieldName: TankInfoFieldNames,
    messageText: string
  ) => {
    // Only show message if there are changes in recentlyUpdated arrays
    if (
      updatedRecentlyUpdated?.fields.length > 0 ||
      Object.values(updatedRecentlyUpdated?.products ?? {}).some(
        (product: any) => product.length > 0
      )
    ) {
      setUpdatedField(inputFieldName, {
        updateMessage: messageText
      });
    }
  };

  const shouldDisplayUpdateMessage = (
    inputFieldName: TankInfoFieldNames,
    value: number,
    nonCarrierProducts: Product[]
  ) => {
    if (nonCarrierProducts.length === 0) return false;

    const snapshot = {
      tankSize: inputFieldName === 'tankSize' ? value : values.tankSize,
      tankCount: inputFieldName === 'tankCount' ? value : values.tankCount
    };

    const haveTankSize =
      typeof snapshot.tankSize === 'number' && snapshot.tankSize > 0;
    const haveTankCount =
      typeof snapshot.tankCount === 'number' && snapshot.tankCount > 0;

    if (
      isAtLeastTwoFieldsFilled(inputFieldName, value) &&
      (haveTankSize || haveTankCount)
    ) {
      return true;
    }

    return false;
  };

  const handleInputBlur = (inputFieldName: TankInfoFieldNames) => {
    setFocusedInput(null);

    // Validate and commit the pending edit
    // Remove commas from the value to prevent invalid number format error
    const val = Number(pendingEdits[inputFieldName]?.replaceAll(',', ''));

    if (
      pendingEdits[inputFieldName] !== '' &&
      Number.isFinite(val) &&
      val > 0
    ) {
      const isCarrierValuesValid = validateCarrierValues(inputFieldName, val);
      const isReadyToCalculateValues = !isNotReadyToCalculateDependingValues(
        inputFieldName,
        val
      );

      const nonCarrierProducts = getNonCarrierLiquidProducts(values.products);

      if (isReadyToCalculateValues && !isCarrierValuesValid) {
        const sumOfTotalProducts = calculateTotalProductsByKey(
          nonCarrierProducts,
          'totalProduct'
        );

        const updatedRecentlyUpdated = handleUpdate(
          'totalProduct',
          sumOfTotalProducts
        );

        showUpdateMessageIfChanged(
          updatedRecentlyUpdated,
          inputFieldName,
          'All values must be positive. Tank information updated to lowest possible values.'
        );
      } else {
        const updatedRecentlyUpdated = handleUpdate(inputFieldName, val);

        if (
          shouldDisplayUpdateMessage(inputFieldName, val, nonCarrierProducts)
        ) {
          showUpdateMessageIfChanged(
            updatedRecentlyUpdated,
            inputFieldName,
            'Changes successful.'
          );
        }
      }
    } else {
      // If invalid input, fallback to existing value
      const existingValue = values[inputFieldName];
      if (existingValue !== undefined) {
        handleUpdate(inputFieldName, existingValue);
      }
    }

    setPendingEdits(prev => {
      const copy = { ...prev };
      delete copy[inputFieldName];
      return copy;
    });
  };

  const setRecentlyUpdatedClassNames = (fieldName: TankInfoFieldNames) => {
    if (!isUpdatingTankInfo || !recentlyUpdated?.fields?.includes(fieldName))
      return '';

    return RECENTLY_UPDATED_CLASSNAMES;
  };

  const tankInformationRows: FormRowConfig[] = [
    {
      id: 'tank-information-row',
      fields: [
        {
          id: 'tankSize',
          label: 'Tank Size',
          colSpan: COL_SPAN_ONE_FOURTH,
          required: false,
          error: errors.tankSize,
          render: () => (
            <Input
              data-testid="tank-size-input"
              className="w-full"
              name="tankSize"
              size="middle"
              min={0}
              step={1}
              status={errors.tankSize ? 'error' : undefined}
              value={getDisplayValue(values.tankSize, 'tankSize')}
              onFocus={handleInputFocus}
              onChange={handleInputChange}
              onBlur={() => {
                handleInputBlur('tankSize');
              }}
              suffix={<span className="text-neutral-60">gal</span>}
            />
          )
        },
        {
          id: 'tankCount',
          label: 'Tank Count',
          colSpan: COL_SPAN_ONE_FOURTH,
          required: false,
          error: errors.tankCount,
          divider: true,
          render: () => (
            <Input
              data-testid="tank-count-input"
              style={{ width: 'calc(100% - 12px)' }}
              name="tankCount"
              size="middle"
              min={0}
              step={1}
              status={errors.tankCount ? 'error' : undefined}
              value={getDisplayValue(values.tankCount, 'tankCount')}
              onFocus={handleInputFocus}
              onChange={handleInputChange}
              onBlur={() => {
                handleInputBlur('tankCount');
              }}
              suffix={<span className="text-neutral-60">tanks</span>}
              className={setRecentlyUpdatedClassNames('tankCount')}
            />
          )
        },
        {
          id: 'overallRatePerAcre',
          label: (
            <Row prefixCls="syt-antd-row">
              Overall Rate per Acre
              <InfoTooltip
                className="ml-1"
                placement="top"
                title={
                  LayoutStrings.products.tankInfo.overallRatePerAcreTooltip
                }
              />
            </Row>
          ),
          colSpan: COL_SPAN_ONE_FOURTH,
          colStyle: { paddingLeft: '24px' },
          required: false,
          error: errors.overallRatePerAcre,
          render: () => (
            <Input
              data-testid="overall-rate-per-acre-input"
              className={`w-full ${setRecentlyUpdatedClassNames(
                'overallRatePerAcre'
              )}`}
              name="overallRatePerAcre"
              size="middle"
              min={0}
              status={errors.overallRatePerAcre ? 'error' : undefined}
              value={getDisplayValue(
                values.overallRatePerAcre,
                'overallRatePerAcre'
              )}
              onFocus={handleInputFocus}
              onChange={handleInputChange}
              onBlur={() => {
                handleInputBlur('overallRatePerAcre');
              }}
              suffix={<span className="text-neutral-60">gal/ac</span>}
            />
          )
        },
        {
          id: 'totalProduct',
          label: (
            <Row prefixCls="syt-antd-row">
              Overall Total Product
              <InfoTooltip
                className="ml-1"
                placement="top"
                title={
                  LayoutStrings.products.tankInfo.overallTotalProductTooltip
                }
              />
            </Row>
          ),
          colSpan: COL_SPAN_ONE_FOURTH,
          required: false,
          error: errors.totalProduct,
          render: () => (
            <Input
              data-testid="total-product-input"
              className={`w-full ${setRecentlyUpdatedClassNames(
                'totalProduct'
              )}`}
              name="totalProduct"
              size="middle"
              min={0}
              status={errors.totalProduct ? 'error' : undefined}
              value={getDisplayValue(values.totalProduct, 'totalProduct')}
              onFocus={handleInputFocus}
              onChange={handleInputChange}
              onBlur={() => {
                handleInputBlur('totalProduct');
              }}
              suffix={<span className="text-neutral-60">gal</span>}
            />
          )
        }
      ]
    }
  ];

  return (
    <div className="tank-information-section">
      <FormGrid rows={tankInformationRows} showErrorLineConditionally />
    </div>
  );
};

export default TankInformationSection;
